import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as DelteIcon } from '../../../../../../../../assets/images/delete_file.svg';
import { Box } from '@mui/system';
import ModalwithButton from '../../../../../../../../components/Modal/modalwithButton';

const TableComp = ({
  columns,
  rows,
  firstColumnBgColor,
  showTotal,
  actionIcon,
  loader,
  handleActionClick,
  noRecordsFound,
  statusChange,
  statusColumn,
  lockIcon,
  editDisabled = false,
  lockIconClick,
  isformatedRows = false,
  handleDelete = null,
  deleteMessage = 'Are you sure?',
  deleteSubMessage = 'Are you sure you want to delete this record?',
}) => {
  const [loading, setLoading] = useState(true);
  const [confirmDeleteRow, setConfirmDeleteRow] = useState(null);
  const columnlength =
    columns.length + (actionIcon ? 1 : 0) + (statusColumn ? 1 : 0);
  useEffect(() => {
    if (loader) {
      setLoading(loader);
    } else {
      setLoading(false);
    }
  }, [rows, loader]);

  const calculateTotal = field => {
    return rows?.reduce((total, row) => total + row[field], 0);
  };

  const totalRow = columns?.reduce((acc, column) => {
    if (column.field) {
      acc[column.field] = calculateTotal(column.field);
    }

    return acc;
  }, {});

  return (
    <>
      <TableContainer sx={{ border: '1px solid #E4E4E4', borderRadius: '8px' }}>
        <Table aria-label="common table">
          <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  className="text-medium-12px"
                  key={index}
                  sx={
                    index === 0
                      ? { backgroundColor: firstColumnBgColor, color: '#7D778C' }
                      : { color: '#7D778C' }
                  }>
                  {column.label}
                </TableCell>
              ))}
              {statusColumn && (
                <TableCell
                  className="text-medium-12px"
                  sx={{ backgroundColor: firstColumnBgColor, color: '#7D778C' }}
                  key={'status'}>
                  Status
                </TableCell>
              )}
              {actionIcon && (
                <TableCell
                  className="text-medium-12px"
                  sx={{ backgroundColor: firstColumnBgColor, color: '#7D778C' }}
                  key={'action'}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnlength} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : noRecordsFound || rows?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnlength} align="center">
                  No record found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column, colIndex) => (
                    <TableCell
                      className="text-medium-12px"
                      key={colIndex}
                      sx={
                        colIndex === 0
                          ? { backgroundColor: firstColumnBgColor }
                          : {}
                      }>
                      {column.format
                        ? column.format(row[column.field])
                        : row[column.field] ?? '-'}
                    </TableCell>
                  ))}
                  {statusColumn && (
                    <TableCell className="text-medium-12px" key={'statusButton'}>
                      <Switch
                        checked={row?.status}
                        size="small"
                        onChange={() => statusChange(row)}
                        disabled={editDisabled}
                      />
                    </TableCell>
                  )}

                  {actionIcon && (
                    <TableCell className="text-medium-12px" key={'actionButton'}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                        {typeof lockIcon === 'function' && (
                          <span
                            key={'lockButton'}
                            style={{ cursor: 'default' }}
                            onClick={id => {
                              id.stopPropagation();
                              lockIconClick(row);
                            }}>
                            {lockIcon(row)}
                          </span>
                        )}

                        <span
                          onClick={id => {
                            id.stopPropagation();
                            if (!editDisabled) handleActionClick(!isformatedRows ? row : rowIndex);
                          }}
                          style={{
                            cursor: editDisabled ? 'not-allowed' : 'pointer',
                          }}>
                          {actionIcon}
                        </span>
                        {handleDelete && rowIndex === rows.length - 1 && (
                          <Box
                            sx={{
                              cursor: editDisabled ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginLeft: '20px',
                              opacity: editDisabled ? 0.5 : 1,
                            }}
                            onClick={() => {
                              if (!editDisabled) setConfirmDeleteRow(row);
                            }}
                          >
                            <DelteIcon />
                          </Box>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
            {rows.length !== 0 && showTotal && (
              <TableRow>
                <TableCell sx={{ backgroundColor: firstColumnBgColor }}>
                  Total
                </TableCell>
                {columns.slice(1).map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    {column.format
                      ? column.format(totalRow[column.field])
                      : totalRow[column.field]}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ModalwithButton
        open={Boolean(confirmDeleteRow)}
        onClose={() => setConfirmDeleteRow(null)}
        message={deleteMessage}
        subMessage={deleteSubMessage}
        onClickButton={() => {
          handleDelete(confirmDeleteRow);
          setConfirmDeleteRow(null);
        }}
        value="Yes, Delete"
        cancelBtn={true}
      />
    </>
  );
};

TableComp.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      format: PropTypes.func,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  firstColumnBgColor: PropTypes.string,
  showTotal: PropTypes.bool,
  actionIcon: PropTypes.any,
  lockIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  loader: PropTypes.bool,
  handleActionClick: PropTypes.any,
  noRecordsFound: PropTypes.bool,
  statusColumn: PropTypes.any,
  statusChange: PropTypes.any,
  editDisabled: PropTypes.any,
  isformatedRows: PropTypes.bool,
  lockIconClick: PropTypes.func,
  handleDelete: PropTypes.func,
  deleteMessage: PropTypes.string,
  deleteSubMessage: PropTypes.string,
};

TableComp.defaultProps = {
  firstColumnBgColor: '#F9FAFB',
  showTotal: false,
};

export default TableComp;
